import { computed, Injectable, Signal, signal, untracked } from '@angular/core';
import { defer, Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';

export type LoadingScope = 'tree' | 'table';

@Injectable()
export class LoadingStoreService {
  readonly #active = signal<ReadonlyMap<string, LoadingScope | null>>(new Map());

  public readonly loading = computed<boolean>(() => this.#active().size > 0);

  public isLoading(scope: LoadingScope): Signal<boolean> {
    return computed<boolean>(() => [...this.#active().values()].includes(scope));
  }

  private start(id: string, scope: LoadingScope | null): void {
    this.#active.update((active) => new Map(active).set(id, scope));
  }

  private stop(id: string): void {
    this.#active.update((active) => {
      const next = new Map(active);
      next.delete(id);

      return next;
    });
  }

  private generateId(): string {
    return Math.random().toString(36).substring(2, 12) + Date.now().toString(36);
  }

  public loadingOperator<T>(options: { id?: string; scope?: LoadingScope } = {}): (source: Observable<T>) => Observable<T> {
    const { id = this.generateId(), scope = null } = options;

    return (source: Observable<T>) =>
      defer(() => {
        this.start(id, scope);
        return source.pipe(finalize(() => this.stop(id)));
      });
  }

  public manualToggle(id: string, state: boolean, scope: LoadingScope | null = null): void {
    const isActive = untracked(() => this.#active().has(id));

    if (state && !isActive) this.start(id, scope);
    if (!state && isActive) this.stop(id);
  }
}
